import { ActionIcon, Group } from "@mantine/core";
import { IconFileTypeXls, IconTrash } from "@tabler/icons-react";
import { useEffect } from "react";
import type { Control, FieldError, FieldPath } from "react-hook-form";
import { useToast } from "../../../../../../mantine/notifications/useToast";
import type { FormInputData } from "../../../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormFieldController } from "../../../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { LoadOrError } from "../../../../../LoadOrError/LoadOrError";
import type { SiteForForm } from "../../SiteForm.constants";
import { BatchImportErrorDisplay } from "./BatchImportErrorDisplay/BatchImportErrorDisplay";
import { useValidateBatchTemplate } from "./hooks/useValidateBatchTemplate";

interface BatchImportUploadProps {
  data: FormInputData<SiteForForm, FieldPath<SiteForForm>>;
  control: Control<SiteForForm>;
  error: FieldError | undefined;
  template: File | undefined;
  onReset: () => void;
  onTemplateHasErrors: (value: boolean) => void;
}

function BatchImportUpload({
  data,
  control,
  error,
  template,
  onReset,
  onTemplateHasErrors
}: BatchImportUploadProps) {
  const { showToast } = useToast();
  const {
    data: checkErrors,
    isLoading,
    error: fetchError
  } = useValidateBatchTemplate(template);

  useEffect(() => {
    if (typeof checkErrors !== "undefined") {
      const hasErrors = checkErrors.length > 0;

      if (!hasErrors) {
        showToast({
          type: "success",
          message: "Erfassungsbogen erfolgreich hochgeladen"
        });
      }

      onTemplateHasErrors(hasErrors);
    }
  }, [checkErrors, onTemplateHasErrors, showToast]);

  return (
    <>
      <FormFieldController control={control} data={data} error={error} />
      <LoadOrError error={fetchError} loading={isLoading}>
        {checkErrors && checkErrors.length > 0 && (
          <BatchImportErrorDisplay errors={checkErrors} />
        )}
        {template && (
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <IconFileTypeXls size="1.2rem" />
              {template.name}
            </Group>
            <ActionIcon
              color="error"
              size="xs"
              variant="transparent"
              onClick={onReset}
            >
              <IconTrash />
            </ActionIcon>
          </Group>
        )}
      </LoadOrError>
    </>
  );
}

export { BatchImportUpload };
